import { useState } from 'react';
import { ActionIcon, ColorInput, Flex, Group, NumberInput, Stack, Switch } from '@mantine/core';
import { IconBold, IconItalic } from '@tabler/icons-react';
import type { ExportBrandingConfigData, FontConfig, HeadingStyleConfig } from '../types';
import FontSelect from './FontSelect';
import SectionHeader from './SectionHeader';
import StyleRow from './StyleRow';

type Props = {
  config: ExportBrandingConfigData;
  onChange: (config: ExportBrandingConfigData) => void;
};

type StyleDef =
  | { key: 'normal' | 'body_text'; label: string; type: 'body' }
  | {
      key: 'heading_1' | 'heading_2' | 'heading_3' | 'heading_4';
      label: string;
      type: 'heading';
    };

const STYLES: StyleDef[] = [
  { key: 'normal', label: 'Normal', type: 'body' },
  { key: 'body_text', label: 'Body Text', type: 'body' },
  { key: 'heading_1', label: 'Heading 1', type: 'heading' },
  { key: 'heading_2', label: 'Heading 2', type: 'heading' },
  { key: 'heading_3', label: 'Heading 3', type: 'heading' },
  { key: 'heading_4', label: 'Heading 4', type: 'heading' },
];

function BodyControls({
  value,
  onChange,
}: {
  value: FontConfig;
  onChange: (v: FontConfig) => void;
}) {
  return (
    <Stack gap="sm">
      <Group grow>
        <FontSelect
          value={value.font_family}
          onChange={v => onChange({ ...value, font_family: v })}
        />
        <NumberInput
          label="Size (pt)"
          value={value.font_size_pt}
          onChange={v => onChange({ ...value, font_size_pt: Number(v) || 11 })}
          min={6}
          max={36}
          step={0.5}
        />
      </Group>
      <Flex align="center" gap="sm">
        <ColorInput
          label="Text colour"
          format="hex"
          value={`#${value.text_color}`}
          onChange={v => onChange({ ...value, text_color: v.replace('#', '') })}
          style={{ flex: 1 }}
        />
        <Flex align="center" gap={4} mt={24}>
          <ActionIcon
            variant={value.bold ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, bold: !value.bold })}
            size="lg"
          >
            <IconBold size={18} />
          </ActionIcon>
          <ActionIcon
            variant={value.italic ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, italic: !value.italic })}
            size="lg"
          >
            <IconItalic size={18} />
          </ActionIcon>
        </Flex>
      </Flex>
    </Stack>
  );
}

function HeadingControls({
  value,
  onChange,
}: {
  value: HeadingStyleConfig;
  onChange: (v: HeadingStyleConfig) => void;
}) {
  return (
    <Stack gap="sm">
      <Group grow>
        <FontSelect
          value={value.font_family}
          onChange={v => onChange({ ...value, font_family: v })}
        />
        <NumberInput
          label="Size (pt)"
          value={value.font_size_pt}
          onChange={v => onChange({ ...value, font_size_pt: Number(v) || 11 })}
          min={6}
          max={72}
          step={0.5}
        />
      </Group>
      <Flex align="center" gap="sm">
        <ColorInput
          label="Text colour"
          format="hex"
          value={`#${value.text_color}`}
          onChange={v => onChange({ ...value, text_color: v.replace('#', '') })}
          style={{ flex: 1 }}
        />
        <Flex align="center" gap={4} mt={24}>
          <ActionIcon
            variant={value.bold ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, bold: !value.bold })}
            size="lg"
          >
            <IconBold size={18} />
          </ActionIcon>
          <ActionIcon
            variant={value.italic ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, italic: !value.italic })}
            size="lg"
          >
            <IconItalic size={18} />
          </ActionIcon>
        </Flex>
      </Flex>
      <Group>
        <Switch
          label="Bottom border"
          checked={value.bottom_border_enabled}
          onChange={e => onChange({ ...value, bottom_border_enabled: e.currentTarget.checked })}
        />
        {value.bottom_border_enabled && (
          <ColorInput
            label="Border colour"
            format="hex"
            value={`#${value.bottom_border_color}`}
            onChange={v => onChange({ ...value, bottom_border_color: v.replace('#', '') })}
            style={{ flex: 1 }}
          />
        )}
      </Group>
    </Stack>
  );
}

export default function TypographySection({ config, onChange }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const toggle = (key: string) => setExpanded(prev => (prev === key ? null : key));

  return (
    <div>
      <SectionHeader
        title="Typography"
        subtitle="Configure the font, size, and colour for body text and heading styles."
      />
      <Stack gap="xxs">
        {STYLES.map(style => {
          const value = config[style.key];
          const traits = [
            `${value.font_family}`,
            `${value.font_size_pt}pt`,
            ...(value.bold ? ['Bold'] : []),
            ...(value.italic ? ['Italic'] : []),
          ];
          const summary = traits.join(', ');

          return (
            <StyleRow
              key={style.key}
              label={style.label}
              summary={summary}
              colorHex={value.text_color}
              expanded={expanded === style.key}
              onToggle={() => toggle(style.key)}
            >
              {style.type === 'body' ? (
                <BodyControls
                  value={value as FontConfig}
                  onChange={v => onChange({ ...config, [style.key]: v })}
                />
              ) : (
                <HeadingControls
                  value={value as HeadingStyleConfig}
                  onChange={v => onChange({ ...config, [style.key]: v })}
                />
              )}
            </StyleRow>
          );
        })}
      </Stack>
    </div>
  );
}
